import _ from 'lodash'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import { Consultation } from '@/core/models/consultation.model'
import documentModel, { Document, ObjectType, objectTypes, RecordingAnnotation } from '@/core/models/document.model'
import { Patient } from '@/core/models/patient.model'

import { RootState } from '@/redux/rootReducer'
import { consultationUpdatedSocketEvent, selectConsultationById, updateConsultationSuccess } from '@/redux/reducers/consultation.reducer'
import {
  addDocument as addDocumentAction,
  addDictaphoneDocument as addDictaphoneDocumentAction,
  addDocumentSuccess as addDocumentSuccessAction,
  addDocumentFailure as addDocumentFailureAction,
  deleteDocuments as deleteDocumentsAction,
  deleteDocumentsSuccess as deleteDocumentsSuccessAction,
  deleteDocumentsFailure as deleteDocumentsFailureAction,
  generateQrCode as generateQrCodeAction,
  generateQrCodeFailure as generateQrCodeFailureAction,
  generateQrCodeSuccess as generateQrCodeSuccessAction,
  updateFilename as updateFilenameAction,
  updateFilenameSucesss as updateFilenameSucesssAction,
  updateFilenameFailure as updateFilenameFailureAction,
  sendDocumentsByEmail as sendDocumentsByEmailAction,
  sendDocumentsByEmailSucess as sendDocumentByEmailSucessAction,
  sendDocumentsByEmailFailure as sendDocumentByEmailFailureAction,
  addBiologicalDocument as addBiologicalDocumentAction,
  updateRecordingAnnotation as updateRecordingAnnotationAction,
  updateRecordingAnnotationSuccess as updateRecordingAnnotationSuccessAction,
  updateRecordingAnnotationFailure as updateRecordingAnnotationFailureAction,
  deleteRecordingAnnotation as deleteRecordingAnnotationAction,
  deleteRecordingAnnotationSuccess as deleteRecordingAnnotationSuccessAction,
  deleteRecordingAnnotationFailure as deleteRecordingAnnotationFailureAction,
  addPrescription as addPrescriptionAction,
  addPrescriptionSuccess as addPrescriptionSuccessAction,
  addPrescriptionFailure as addPrescriptionFailureAction,
  getFileByKey as getFileByKeyAction,
  getFileByKeySuccess as getFileByKeySuccessAction,
  getFileByKeyFailure as getFileByKeyFailureAction,
  getFileByObject as getFileByObjectAction,
  getFileByObjectSuccess as getFileByObjectSuccessAction,
  getFileByObjectFailure as getFileByObjectFailureAction,
  findFilesByCriteria as findFilesByCriteriaAction,
  findFilesByCriteriaSuccess as findFilesByCriteriaSuccessAction,
  findFilesByCriteriaFailure as findFilesByCriteriaFailureAction,
} from '@/redux/reducers/document.reducer'
import { selectPatientById, updatePatientSuccess } from '@/redux/reducers/patient.reducer'

import { handleError } from '@/utils/errors'
import { Expertise } from '../../core/models/expertise.model'
import { selectExpertiseById, updateExpertiseSuccess } from '../reducers/expertise.reducer'
import { selectEstablishmentById, updateEstablishmentSuccess } from '../reducers/admin.reducer'
import { Establishment } from '@/core/models/establishment.model'

function* addDocument(action: PayloadAction<{ documentFile: File; isRecording?: boolean; objectType: ObjectType; objectId: string }>) {
  try {
    const { documentFile, isRecording = false, objectType, objectId } = action.payload
    const createPresignedUrlRequest = {
      fileName: documentFile.name,
      fileType: documentFile.type,
      objectType,
      objectId,
    }
    const { url: presignedUrl, fileName: filename } = yield call(documentModel.generatePresignedUrl, createPresignedUrlRequest)
    yield call(documentModel.uploadDocumentToAwsS3, presignedUrl, documentFile)
    const pathname = _.deburr(decodeURIComponent(new URL(presignedUrl).pathname.slice(1)))
    const key = pathname.substring(pathname.indexOf(objectType))
    const documentDto = {
      filename,
      isRecording,
      key,
      objectType,
    }
    const document: Document = yield call(documentModel.addDocument, documentDto, objectType, objectId)
    yield put(addDocumentSuccessAction({ document, objectId }))
    switch (objectType) {
      case 'consultation':
        const consultation: Consultation = yield select((state: RootState) => selectConsultationById(state, objectId))
        const updatedConsultation = {
          ...consultation,
          files: _.uniqBy([...consultation.files, document], 'key'),
        }
        yield put(updateConsultationSuccess(updatedConsultation))
        yield put(consultationUpdatedSocketEvent(updatedConsultation))
        break
      case 'patient':
        const patient: Patient = yield select((state: RootState) => selectPatientById(state, objectId))
        const updatedPatient = {
          ...patient,
          files: patient?.files ? _.uniqBy([...patient.files, document], 'key') : [document],
        }
        yield put(updatePatientSuccess(updatedPatient))
        break
      case 'expertise':
        const expertise: Expertise = yield select((state: RootState) => selectExpertiseById(state, objectId))
        const updatedExpertise = {
          ...expertise,
          files: expertise?.files ? _.uniqBy([...expertise.files, document], 'key') : [document],
        }
        yield put(updateExpertiseSuccess(updatedExpertise))
        break
      case 'establishment':
        const establishment: Establishment = yield select((state: RootState) => selectEstablishmentById(state, objectId))
        const updatedEstablishment = {
          ...establishment,
          meetingBackgrounds: establishment.meetingBackgrounds ? _.uniqBy([...(establishment.meetingBackgrounds || []), document], 'key') : [document],
        }
        yield put(updateEstablishmentSuccess(updatedEstablishment))
        break
      default:
        break
    }
  } catch (exception) {
    console.warn(exception)
    yield put(addDocumentFailureAction(handleError(exception)))
  }
}

function* addDictaphoneDocument(action: PayloadAction<{ documentFile: File; objectId: string }>) {
  try {
    const { documentFile, objectId } = action.payload
    const createPresignedUrlRequest = {
      fileName: documentFile.name,
      fileType: documentFile.type,
      objectType: objectTypes['CONSULTATION'],
      objectId,
    }
    const { url: presignedUrl } = yield call(documentModel.generatePresignedUrl, createPresignedUrlRequest)
    yield call(documentModel.uploadDocumentToAwsS3, presignedUrl, documentFile)
    const documentDto = {
      filename: documentFile.name,
      key: _.deburr(decodeURIComponent(new URL(presignedUrl).pathname.slice(1))),
      objectType: objectTypes['CONSULTATION'],
    }

    yield call(documentModel.addDictaphoneDocument, documentDto, objectId)
  } catch (exception) {
    console.warn(exception)
    yield put(addDocumentFailureAction(handleError(exception)))
  }
}

function* deleteDocuments(action: PayloadAction<{ documents: Omit<Document, 'url'>[]; objectType: ObjectType; objectId: string }>) {
  try {
    const { documents, objectId, objectType } = action.payload
    yield call(documentModel.deleteDocuments, documents, objectType, objectId)
    yield put(deleteDocumentsSuccessAction())
    // TODO: remove when socket implemented
    switch (objectType) {
      case 'consultation':
        const consultation: Consultation = yield select((state: RootState) => selectConsultationById(state, objectId))
        const updatedConsultation = {
          ...consultation,
          files: _.reduce(
            _.difference(_.map(consultation.files, 'key'), _.map(documents, 'key')),
            (result: Document[], key: string) => {
              const file = _.find(consultation.files, ['key', key])
              if (file) result.push(file)
              return result
            },
            []
          ),
        }
        yield put(updateConsultationSuccess(updatedConsultation))
        yield put(consultationUpdatedSocketEvent(updatedConsultation))
        break
      case 'patient':
        const patient: Patient = yield select((state: RootState) => selectPatientById(state, objectId))
        const updatedPatient = {
          ...patient,
          files: _.reduce(
            _.difference(_.map(patient?.files, 'key'), _.map(documents, 'key')),
            (result: Document[], key: string) => {
              const file = _.find(patient?.files, ['key', key])
              if (file) result.push(file)
              return result
            },
            []
          ),
        }
        yield put(updatePatientSuccess(updatedPatient))
        break
      case 'expertise':
        const expertise: Expertise = yield select((state: RootState) => selectExpertiseById(state, objectId))
        const updatedExpertise = {
          ...expertise,
          files: _.reduce(
            _.difference(_.map(expertise?.files, 'key'), _.map(documents, 'key')),
            (result: Document[], key: string) => {
              const file = _.find(expertise?.files, ['key', key])
              if (file) result.push(file)
              return result
            },
            []
          ),
        }
        yield put(updateExpertiseSuccess(updatedExpertise))
        break
      default:
        break
    }
  } catch (exception) {
    yield put(deleteDocumentsFailureAction(handleError(exception)))
  }
}

function* generateQrCode(action: PayloadAction<{ url: string }>) {
  try {
    const { url } = action.payload
    const res: { url: string } = yield call(documentModel.generateQrCode, url)
    yield put(generateQrCodeSuccessAction({ url: res.url }))
  } catch (exception) {
    yield put(generateQrCodeFailureAction(handleError(exception)))
  }
}

function* updateFilename(action: PayloadAction<{ file: Omit<Document, 'url'>; filename: string; objectType: ObjectType; objectId: string }>) {
  try {
    const { file, filename, objectType, objectId } = action.payload
    switch (objectType) {
      case 'consultation':
        const updatedConsultation: Consultation = yield call(documentModel.updateFilename, file, filename, objectType, objectId)
        yield put(updateFilenameSucesssAction())
        yield put(updateConsultationSuccess(updatedConsultation))
        yield put(consultationUpdatedSocketEvent(updatedConsultation))
        break
      case 'patient':
        const updatedPatient: Patient = yield call(documentModel.updateFilename, file, filename, objectType, objectId)
        yield put(updateFilenameSucesssAction())
        yield put(updatePatientSuccess(updatedPatient))
        break
      case 'expertise':
        const updatedExpertise: Expertise = yield call(documentModel.updateFilename, file, filename, objectType, objectId)
        yield put(updateFilenameSucesssAction())
        yield put(updateExpertiseSuccess(updatedExpertise))
        break
      default:
        break
    }
  } catch (exception) {
    yield put(updateFilenameFailureAction(handleError(exception)))
  }
}

function* updateRecordingAnnotation(action: PayloadAction<{ key: string; annotation: RecordingAnnotation; objectType: ObjectType; objectId: string }>) {
  try {
    const { key, annotation, objectType, objectId } = action.payload
    switch (objectType) {
      case 'consultation':
        const updatedConsultation: Consultation = yield call(documentModel.updateRecordingAnnotation, key, annotation, objectType, objectId)
        yield put(updateRecordingAnnotationSuccessAction())
        yield put(updateConsultationSuccess(updatedConsultation))
        break
      default:
        break
    }
  } catch (exception) {
    yield put(updateRecordingAnnotationFailureAction(handleError(exception)))
  }
}

function* deleteRecordingAnnotation(action: PayloadAction<{ fileKey: string; annotationId: string; objectType: ObjectType; objectId: string }>) {
  try {
    const { fileKey, annotationId, objectType, objectId } = action.payload
    switch (objectType) {
      case 'consultation':
        const updatedConsultation: Consultation = yield call(documentModel.deleteRecordingAnnotation, fileKey, annotationId, objectType, objectId)
        yield put(deleteRecordingAnnotationSuccessAction())
        yield put(updateConsultationSuccess(updatedConsultation))
        break
      default:
        break
    }
  } catch (exception) {
    yield put(deleteRecordingAnnotationFailureAction(handleError(exception)))
  }
}

function* addBiologicalDocument(action: PayloadAction<{ documentFile: File; objectType: ObjectType; objectId: string; biologicalId: string }>) {
  try {
    const { documentFile, objectType, objectId, biologicalId } = action.payload
    const createPresignedUrlRequest = {
      fileName: documentFile.name,
      fileType: documentFile.type,
      objectType,
      objectId,
    }
    const { url: presignedUrl } = yield call(documentModel.generatePresignedUrl, createPresignedUrlRequest)
    yield call(documentModel.uploadDocumentToAwsS3, presignedUrl, documentFile)
    const documentDto = {
      filename: documentFile.name,
      key: _.deburr(decodeURIComponent(new URL(presignedUrl).pathname.slice(1))),
      objectType,
    }
    yield call(documentModel.addBiologicalDocument, documentDto, objectType, objectId, biologicalId)
  } catch (exception) {
    console.warn(exception)
    yield put(addDocumentFailureAction(handleError(exception)))
  }
}

function* sendDocumentsByEmail(action: PayloadAction<{ filenames: string[]; emails: string[]; objectId: string; objectType: ObjectType; pdfPassword?: string }>) {
  try {
    const { objectType, objectId, filenames, emails, pdfPassword } = action.payload
    switch (objectType) {
      case 'consultation':
        yield call(documentModel.sendDocumentsByEmail, objectId, emails, filenames, pdfPassword)
        yield put(sendDocumentByEmailSucessAction())
        break
      default:
        break
    }
  } catch (exception) {
    yield put(sendDocumentByEmailFailureAction(handleError(exception)))
  }
}

function* addPrescription(action: PayloadAction<ReturnType<typeof addPrescriptionAction>['payload']>) {
  try {
    const { consultationId, prescriptionId } = action.payload
    yield call(documentModel.addPrescription, consultationId, prescriptionId)
    yield put(addPrescriptionSuccessAction())
  } catch (exception) {
    console.log('🚀 ~ exception:', exception)
    yield put(addPrescriptionFailureAction(handleError(exception)))
  }
}

// New saga handlers for file retrieval actions
function* getFileByKey(action: PayloadAction<ReturnType<typeof getFileByKeyAction>['payload']>) {
  try {
    const { key } = action.payload
    const document: Document = yield call(documentModel.getFileByKey, key)
    yield put(getFileByKeySuccessAction(document))
  } catch (exception) {
    yield put(getFileByKeyFailureAction(handleError(exception)))
  }
}

function* getFileByObject(action: PayloadAction<ReturnType<typeof getFileByObjectAction>['payload']>) {
  try {
    const { objectType, objectId, filename } = action.payload
    const document: Document = yield call(documentModel.getFileByObject, objectType, objectId, filename)
    yield put(getFileByObjectSuccessAction(document))
  } catch (exception) {
    yield put(getFileByObjectFailureAction(handleError(exception)))
  }
}

function* findFilesByCriteria(action: PayloadAction<ReturnType<typeof findFilesByCriteriaAction>['payload']>) {
  try {
    const { objectType, objectId, filenamePattern } = action.payload
    const documents: Document[] = yield call(documentModel.findFilesByCriteria, objectType, objectId, filenamePattern)
    yield put(findFilesByCriteriaSuccessAction(documents))
  } catch (exception) {
    yield put(findFilesByCriteriaFailureAction(handleError(exception)))
  }
}

const sagas = [
  takeLatest(addDocumentAction.type, addDocument),
  takeLatest(addDictaphoneDocumentAction.type, addDictaphoneDocument),
  takeLatest(deleteDocumentsAction.type, deleteDocuments),
  takeLatest(generateQrCodeAction.type, generateQrCode),
  takeLatest(updateFilenameAction.type, updateFilename),
  takeLatest(sendDocumentsByEmailAction.type, sendDocumentsByEmail),
  takeLatest(addBiologicalDocumentAction.type, addBiologicalDocument),
  takeLatest(updateRecordingAnnotationAction.type, updateRecordingAnnotation),
  takeLatest(deleteRecordingAnnotationAction.type, deleteRecordingAnnotation),
  takeLatest(addPrescriptionAction.type, addPrescription),
  takeLatest(getFileByKeyAction.type, getFileByKey),
  takeLatest(getFileByObjectAction.type, getFileByObject),
  takeLatest(findFilesByCriteriaAction.type, findFilesByCriteria),
]

export default sagas
